import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Play, ArrowRight } from 'lucide-react';

const ScrollSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

const WorkflowsPage = () => {
  const workflows = [
    {
      id: 'social-media',
      title: 'Social Media Management',
      description: 'End-to-end social media strategy, content creation, and publishing.',
      image: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=800&q=80',
      stages: [
        {
          step: '01',
          title: 'Discovery & Audit',
          description: 'We analyze your current presence, competitors, and audience to understand where you stand.',
          duration: '3-5 days',
        },
        {
          step: '02',
          title: 'Strategy & Content Pillars',
          description: 'Define your brand voice, content themes, posting frequency, and KPIs.',
          duration: '5-7 days',
        },
        {
          step: '03',
          title: 'Content Creation',
          description: 'Shoot days, editing, graphic design, and copywriting for the month.',
          duration: 'Ongoing',
        },
        {
          step: '04',
          title: 'Review & Approval',
          description: 'Preview all content in your client portal. Approve or request changes.',
          duration: '2-3 days',
        },
        {
          step: '05',
          title: 'Publishing',
          description: 'Scheduled posting across platforms with optimal timing.',
          duration: 'Ongoing',
        },
        {
          step: '06',
          title: 'Reporting & Optimization',
          description: 'Monthly performance report with insights and next-month improvements.',
          duration: 'Monthly',
        },
      ],
    },
    {
      id: 'podcast',
      title: 'Podcast Production',
      description: 'From concept to distribution, we handle your entire podcast journey.',
      image: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=800&q=80',
      stages: [
        {
          step: '01',
          title: 'Concept & Planning',
          description: 'Define your podcast format, target audience, episode structure, and branding.',
          duration: '1 week',
        },
        {
          step: '02',
          title: 'Topic Research',
          description: 'Plan episode topics, guest outreach, and talking points.',
          duration: 'Per episode',
        },
        {
          step: '03',
          title: 'Recording',
          description: 'Professional recording setup, either in-studio or guided remote.',
          duration: '1-2 hours',
        },
        {
          step: '04',
          title: 'Editing & Production',
          description: 'Audio editing, intro/outro, sound design, and quality enhancement.',
          duration: '3-5 days',
        },
        {
          step: '05',
          title: 'Review',
          description: 'Preview the final episode and request any changes.',
          duration: '2 days',
        },
        {
          step: '06',
          title: 'Distribution',
          description: 'Publish to Spotify, Apple Podcasts, YouTube, and other platforms.',
          duration: '1 day',
        },
      ],
    },
    {
      id: 'video-production',
      title: 'Video Production',
      description: 'Professional video content from concept to final delivery.',
      image: 'https://images.unsplash.com/photo-1705107958681-db6d591b57cb?w=800&q=80',
      stages: [
        {
          step: '01',
          title: 'Pre-Production',
          description: 'Script development, storyboarding, location scouting, and crew planning.',
          duration: '1-2 weeks',
        },
        {
          step: '02',
          title: 'Production (Shoot)',
          description: 'Professional shoot with our team, equipment, and direction.',
          duration: '1-3 days',
        },
        {
          step: '03',
          title: 'Editing',
          description: 'Assembly cut, rough cut, and fine cut with your feedback.',
          duration: '1-2 weeks',
        },
        {
          step: '04',
          title: 'Color Grading',
          description: 'Professional color correction and grading for cinematic look.',
          duration: '2-3 days',
        },
        {
          step: '05',
          title: 'Sound Design & Music',
          description: 'Audio mixing, sound effects, and licensed music.',
          duration: '2-3 days',
        },
        {
          step: '06',
          title: 'Review & Delivery',
          description: 'Final review, revisions, and delivery in required formats.',
          duration: '3-5 days',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen py-32">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 mb-24">
        <ScrollSection>
          <p className="text-primary font-mono text-sm tracking-widest mb-4">OUR WORKFLOWS</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-syne font-bold text-white mb-6">
            Transparent process,<br />
            <span className="text-gradient-gold">premium results</span>
          </h1>
          <p className="text-fp-textMuted text-lg max-w-2xl mb-8">
            Every project follows a proven workflow designed for clarity, collaboration, 
            and exceptional output. See exactly how we work.
          </p>
          <Link
            to="/demo"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="workflow-demo-btn"
          >
            <Play className="w-5 h-5" />
            See Live Demo
          </Link>
        </ScrollSection>
      </section>

      {/* Workflows */}
      {workflows.map((workflow, workflowIndex) => (
        <section key={workflow.id} className="mb-32">
          {/* Workflow Header */}
          <div className="max-w-7xl mx-auto px-6 mb-12">
            <ScrollSection>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-syne font-bold text-white mb-4">
                    {workflow.title}
                  </h2>
                  <p className="text-fp-textMuted text-lg">{workflow.description}</p>
                </div>
                <div className="relative">
                  <img
                    src={workflow.image}
                    alt={workflow.title}
                    className="w-full aspect-video object-cover rounded-sm"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-fp-bg/60 to-transparent rounded-sm" />
                </div>
              </div>
            </ScrollSection>
          </div>

          {/* Timeline */}
          <div className="max-w-7xl mx-auto px-6">
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-white/10 -translate-x-1/2" />

              {workflow.stages.map((stage, stageIndex) => (
                <ScrollSection key={stage.step} delay={stageIndex * 0.1}>
                  <div className={`relative flex items-start gap-8 mb-12 ${
                    stageIndex % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    {/* Timeline Dot */}
                    <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-primary rounded-full -translate-x-1/2 z-10" />

                    {/* Content */}
                    <div className={`ml-16 md:ml-0 md:w-1/2 ${
                      stageIndex % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'
                    }`}>
                      <div className="glass p-6 rounded-sm">
                        <span className="font-mono text-primary text-sm">{stage.step}</span>
                        <h3 className="font-syne font-bold text-white text-xl mt-2 mb-3">
                          {stage.title}
                        </h3>
                        <p className="text-fp-textMuted text-sm mb-4">{stage.description}</p>
                        <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs rounded-sm">
                          {stage.duration}
                        </span>
                      </div>
                    </div>

                    {/* Spacer for alternating layout */}
                    <div className="hidden md:block md:w-1/2" />
                  </div>
                </ScrollSection>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="max-w-7xl mx-auto px-6 mt-12">
            <ScrollSection>
              <div className="flex justify-center">
                <Link
                  to="/get-proposal"
                  className="btn-ghost flex items-center gap-2"
                  data-testid={`workflow-cta-${workflow.id}`}
                >
                  Get a Proposal for {workflow.title}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </ScrollSection>
          </div>
        </section>
      ))}

      {/* Final CTA */}
      <section className="py-24 bg-fp-surface">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <ScrollSection>
            <h2 className="text-3xl md:text-4xl font-syne font-bold text-white mb-6">
              Experience our workflow firsthand
            </h2>
            <p className="text-fp-textMuted text-lg mb-10">
              Try our demo client portal to see exactly how you'll collaborate with us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/demo"
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="workflow-final-demo-btn"
              >
                <Play className="w-5 h-5" />
                Try Demo Portal
              </Link>
              <Link
                to="/get-proposal"
                className="btn-secondary flex items-center justify-center gap-2"
              >
                Get a Proposal
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </ScrollSection>
        </div>
      </section>
    </div>
  );
};

export default WorkflowsPage;
